import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Icon } from "@iconify/react";
import dayjs from "dayjs";

export function SaleConfirmed({ ticket, method, change, cashPayments = [], client, onContinue }) {
  const total = Number(ticket.total);
  const lines = ticket.lines || [];
  const pagosList = (ticket.pagos && ticket.pagos.length > 0)
    ? ticket.pagos
    : (method ? [{ tipopago: method, importe: total }] : []);

  const hasCashPayments = cashPayments.length > 0;
  const totalEntregado = cashPayments.reduce((acc, c) => acc + c.entregado, 0);
  const totalChange = cashPayments.reduce((acc, c) => acc + c.change, 0);
  const singleDelivered = typeof change === "number" && change >= 0 ? total + change : null;

  return (
    <div className="h-full w-full bg-neutral-50 p-6 animate-fade animate-once flex flex-col gap-6 select-none">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Icon icon="mdi:check-circle" className="text-7xl text-green-500" />
          <div>
            <div className="text-4xl font-bold text-neutral-900 leading-tight">Venta realizada</div>
            <div className="text-xl text-neutral-500">
              Ticket #{ticket.exercise_id}/{ticket.reference} · {dayjs(ticket.created_at || new Date()).format("DD/MM/YYYY HH:mm")}
            </div>
          </div>
        </div>
        {client?.name && (
          <Button variant="outline" size="lg" className="h-16 px-6 gap-3">
            <Icon icon="mdi:account" className="text-3xl text-blue-600" />
            <span className="text-2xl font-semibold text-neutral-800">{client.name}</span>
          </Button>
        )}
      </div>

      <div className="flex flex-1 min-h-0 gap-6">
        <div className="flex-1 flex flex-col min-h-0">
          <div className="text-2xl font-semibold text-neutral-900 mb-4">Resumen de la venta</div>
          <div className="flex-1 overflow-y-auto space-y-3 scrollbar-thin scrollbar-thumb-neutral-300 scrollbar-thumb-rounded-lg scrollbar-track-rounded-lg">
            {lines.map((line, index) => (
              <div key={index} className="bg-white rounded-xl border p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-lg bg-neutral-100 flex items-center justify-center text-3xl font-bold text-neutral-700 shrink-0">
                    {line.quantity}
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-neutral-900">{line.products.name}</div>
                    {(line.mods || []).length > 0 && (
                      <div className="text-lg text-neutral-500 mt-1">
                        {(line.mods || []).map((mod, i) => (
                          <div key={i}>+ {mod.mod.defaultEnabled ? "Sin " : "Con "}{mod.mod.name}</div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-3xl font-bold text-neutral-900 shrink-0">{(line.custom_price * line.quantity).toFixed(2)}€</div>
              </div>
            ))}
          </div>
        </div>

        <div className="w-[400px] flex flex-col gap-4">
          {hasCashPayments && (
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-6">
                <div className="text-base text-green-700 mb-3">Entregado en efectivo</div>
                <div className="w-full space-y-3">
                  {cashPayments.map((p, i) => (
                    <div key={i} className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-xl font-semibold text-neutral-800">Pago {i + 1}</div>
                        <div className="text-base text-green-600">Cambio {p.change.toFixed(2)}€</div>
                      </div>
                      <span className="text-4xl font-extrabold text-green-600 leading-none">{p.entregado.toFixed(2)}€</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-6 text-center">
              <div className="text-base text-neutral-500 mb-1">Total</div>
              <div className="text-6xl font-extrabold text-neutral-900 leading-none">{total.toFixed(2)}€</div>
              {pagosList.length > 0 && (
                <div className="w-full space-y-2 mt-4 border-t pt-3">
                  {pagosList.map((pago, i) => (
                    <div key={i} className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <Icon icon={pago.tipopago?.icon || "mdi:cash"} className="text-2xl text-neutral-500" />
                        <span className="text-xl font-semibold text-neutral-800">{pago.tipopago?.name}</span>
                      </div>
                      <span className="text-xl font-bold text-neutral-900">{Number(pago.importe).toFixed(2)}€</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {hasCashPayments && (
            <div className="grid grid-cols-2 gap-3">
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-base text-neutral-500 mb-1">Entregado</div>
                  <div className="text-3xl font-bold text-neutral-900 leading-tight">{totalEntregado.toFixed(2)}€</div>
                </CardContent>
              </Card>
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-4 text-center">
                  <div className="text-base text-green-700 mb-1">Cambio</div>
                  <div className="text-4xl font-extrabold text-green-600 leading-none">{totalChange.toFixed(2)}€</div>
                </CardContent>
              </Card>
            </div>
          )}
          {!hasCashPayments && singleDelivered !== null && (
            <div className="grid grid-cols-2 gap-3">
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-base text-neutral-500 mb-1">Entregado</div>
                  <div className="text-3xl font-bold text-neutral-900 leading-tight">{singleDelivered.toFixed(2)}€</div>
                </CardContent>
              </Card>
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-4 text-center">
                  <div className="text-base text-green-700 mb-1">Cambio</div>
                  <div className="text-4xl font-extrabold text-green-600 leading-none">{change.toFixed(2)}€</div>
                </CardContent>
              </Card>
            </div>
          )}
          <div className="flex-1" />

          <Button variant="default" size="lg" className="w-full h-20 text-2xl font-bold" onClick={onContinue}>
            Nueva venta
          </Button>
        </div>
      </div>
    </div>
  );
}